/**
 * Carbon batch actions (DESIGN.md "Data table"): while rows are selected a
 * Soft Green bar slides down over the toolbar (240ms, entrance curve) with
 * "2 selected", the actions (label, then a trailing icon) in near-black text,
 * a divider and Cancel. Below c-md an action shows its `shortLabel`, but its
 * accessible name stays the full label. At zero it is hidden and inert. It
 * sits in TableToolbar's `batch` slot; DataTable wires it from `batchActions`.
 *
 * Secondary verbs (`overflow`) fold into a "More actions" menu (the overflow
 * kebab) so the bar never clips: below c-md always, and at every width once
 * the bar holds more than three actions. While the menu is there, phone
 * buttons drop their icons to keep the row inside 390px.
 */
import { useState, type ReactNode } from 'react'
import { Menu, Portal } from '@headlessui/react'
import clsx from 'clsx'
import { IconOverflow } from './icons'
import { MenuPanel } from './OverflowMenu'

export interface BatchAction {
  id: string
  label: string
  /** Phone label ("Accept" for "Accept as new baseline"). */
  shortLabel?: string
  icon?: ReactNode
  onClick: () => void
  /** A divider before this action. */
  dividerBefore?: boolean
  disabled?: boolean
  /**
   * A secondary verb: in the "More actions" menu below c-md, and at every width once the bar has more
   * than three actions; otherwise a button like the rest. Primary verbs (unset) always stay buttons.
   */
  overflow?: boolean
}

export interface BatchActionBarProps {
  count: number
  actions: BatchAction[]
  onCancel: () => void
  /** "Select all {total}" when every row on the page is selected and more match. */
  selectAllMatching?: { total: number; onSelectAll: () => void }
  className?: string
}

const ACTION =
  'inline-flex h-12 flex-none items-center gap-3 whitespace-nowrap px-4 text-body text-interactive-on transition-colors duration-70 ease-carbon ' +
  'hover:bg-interactive-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-[color:var(--on-interactive)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50 max-c-md:px-3'

const Divider = ({ className }: { className?: string }) => (
  <span aria-hidden="true" className={clsx('h-4 w-px flex-none bg-[color:color-mix(in_oklab,var(--on-interactive)_45%,transparent)]', className)} />
)

/** More than this many actions and the secondary verbs live in the menu at every width. */
const INLINE_MAX = 3

/** The overflow kebab in the bar's own ink, opening the shared layer-02 menu through a portal (the bar clips). */
function MoreActions({ actions, className }: { actions: BatchAction[]; className?: string }) {
  const [trigger, setTrigger] = useState<HTMLElement | null>(null)
  return (
    <Menu as="div" className={clsx('flex flex-none', className)}>
      {({ open }) => (
        <>
          <Menu.Button
            ref={setTrigger}
            aria-label="More actions"
            className={clsx(
              'grid h-12 w-12 place-items-center text-interactive-on transition-colors duration-70 ease-carbon hover:bg-interactive-hover aria-expanded:bg-interactive-hover',
              'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-[color:var(--on-interactive)]',
            )}
          >
            <IconOverflow />
          </Menu.Button>
          {open ? (
            <Portal>
              <MenuPanel anchor={trigger} align="end" items={actions.map(({ id, label, onClick, disabled }) => ({ id, label, onClick, disabled }))} />
            </Portal>
          ) : null}
        </>
      )}
    </Menu>
  )
}

export function BatchActionBar({ count, actions, onCancel, selectAllMatching, className }: BatchActionBarProps) {
  const shown = count > 0
  const secondary = actions.filter((a) => a.overflow)
  // 'always': the menu at every width; 'phone': secondary verbs are buttons from c-md and the menu below it.
  const menu = secondary.length === 0 ? null : actions.length > INLINE_MAX ? 'always' : 'phone'
  const buttons = menu === 'always' ? actions.filter((a) => !a.overflow) : actions
  return (
    <div
      aria-hidden={shown ? undefined : true}
      className={clsx(
        'flex h-full min-h-12 w-full items-center bg-interactive text-interactive-on transition-[transform,visibility] duration-240 ease-carbon-in',
        shown ? 'pointer-events-auto visible translate-y-0' : 'invisible -translate-y-full',
        className,
      )}
    >
      {/* The count gives way (ellipsis) before an action would be clipped off the bar's end. */}
      <p aria-live="polite" className="min-w-0 truncate px-4 text-body tabular-nums max-c-md:px-3">
        {count} selected
      </p>
      {selectAllMatching && selectAllMatching.total > count ? (
        <button type="button" onClick={selectAllMatching.onSelectAll} className={clsx(ACTION, 'underline-offset-2 hover:underline')}>
          Select all {selectAllMatching.total}
        </button>
      ) : null}
      <span className="min-w-0 flex-1" />
      {buttons.map((action) => (
        <span key={action.id} className={clsx('contents', menu === 'phone' && action.overflow && 'max-c-md:hidden')}>
          {action.dividerBefore ? <Divider /> : null}
          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            aria-label={action.shortLabel ? action.label : undefined}
            className={ACTION}
          >
            {action.shortLabel ? (
              <span>
                <span className="c-md:hidden">{action.shortLabel}</span>
                <span className="max-c-md:hidden">{action.label}</span>
              </span>
            ) : (
              <span>{action.label}</span>
            )}
            {action.icon ? (
              <span aria-hidden="true" className={clsx('grid place-items-center [&>svg]:h-4 [&>svg]:w-4', menu && 'max-c-md:hidden')}>
                {action.icon}
              </span>
            ) : null}
          </button>
        </span>
      ))}
      {menu ? (
        <>
          {secondary[0].dividerBefore ? <Divider className={clsx(menu === 'phone' && 'c-md:hidden')} /> : null}
          <MoreActions actions={secondary} className={clsx(menu === 'phone' && 'c-md:hidden')} />
        </>
      ) : null}
      <Divider />
      <button type="button" onClick={onCancel} className={ACTION}>
        Cancel
      </button>
    </div>
  )
}

export default BatchActionBar
